import {
    IonBadge,
    IonButton,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSpinner,
    IonTitle,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter,
    useIonToast,
    useIonViewWillEnter
} from "@ionic/react";
import {
    addOutline,
    americanFootballOutline,
    arrowForwardOutline,
    copyOutline,
    logOutOutline,
    peopleOutline,
    refreshOutline,
    ticketOutline,
    trophyOutline
} from "ionicons/icons";

import {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";
import Cookies from "js-cookie";
import {
    clearAuthentication,
    isAuthenticated
} from "../services/auth";
import {
    fantasyPushRegisterServiceWorker
} from "../services/fantasyPushNotifications";

import FantasyPushSettings from
    "../components/FantasyPushSettings";

import { useHistory } from "react-router-dom";

import ListContainer from "../components/FantasyContainer";
import "./Fantasy.css";

interface FantasyLeague {
    id: string;
    name: string;
}

function getAccessToken(): string {
    return Cookies.get("access_token") || "";
}

function getAccessUser(): string {
    return Cookies.get("access_user") || "Jugador";
}

function getAccessUsername(): string {
    return Cookies.get("access_username") || "";
}

function getLeaguesFromData(data: unknown): FantasyLeague[] {
    const rawItems: unknown[] = Array.isArray(data)
        ? data.flatMap(function (item) {
              if (
                  item &&
                  typeof item === "object" &&
                  !Array.isArray(item) &&
                  !("id" in item)
              ) {
                  return Object.values(item);
              }

              return [item];
          })
        : data && typeof data === "object"
        ? Object.values(data)
        : [];

    const uniqueLeagues = new Map<string, FantasyLeague>();

    rawItems.forEach(function (item) {
        if (!item || typeof item !== "object") {
            return;
        }

        const league = item as {
            id?: string | number;
            name?: string;
            nombre?: string;
        };

        const leagueId = String(league.id || "");
        const leagueName = String(
            league.name || league.nombre || "Liga sin nombre"
        );

        if (leagueId) {
            uniqueLeagues.set(leagueId, {
                id: leagueId,
                name: leagueName
            });
        }
    });

    return Array.from(uniqueLeagues.values());
}

const Fantasy: React.FC = () => {
    const router = useIonRouter();
    const history = useHistory();
    const [present] = useIonToast();

    const [leagues, setLeagues] = useState<FantasyLeague[]>([]);
    const [leagueId, setLeagueId] = useState("");
    const [loading, setLoading] = useState(true);
    const [joiningLeague, setJoiningLeague] = useState(false);
    const [error, setError] = useState("");

    const [loggingOut, setLoggingOut] = useState(false);

const loggingOutRef = useRef(false);

const leaguesRequestRef = useRef<
    AbortController | null
>(null);

useIonViewWillEnter(
    function () {
        /*
            Si se intenta entrar a Fantasy sin sesión válida,
            nunca se debe mostrar esta pantalla.
        */
        if (!isAuthenticated()) {
            history.replace("/home");
            return;
        }

        /*
            Si la vista quedó cacheada después de un logout
            anterior, se reactiva para la nueva sesión.
        */
        loggingOutRef.current = false;
        setLoggingOut(false);
    }
);

    function getAccessToken(): string {
        return Cookies.get("access_token") || "";
    }

    function getAccessUser(): string {
        return Cookies.get("access_user") || "Jugador";
    }

    function showToast(
        message: string,
        color: "success" | "danger" | "warning"
    ) {
        present({
            message,
            duration: 2200,
            position: "bottom",
            color,
            buttons: [
                {
                    text: "Cerrar",
                    role: "cancel"
                }
            ]
        });
    }

const loadLeagues = useCallback(
    async function (forceRefresh = false) {
        if (loggingOutRef.current) {
            return;
        }

        const userId = getAccessToken();

if (!isAuthenticated()) {
    return;
}

        /*
            Cancela una carga anterior para que una respuesta vieja
            no actualice la pantalla después de cerrar sesión.
        */
        leaguesRequestRef.current?.abort();

        const requestController = new AbortController();

        leaguesRequestRef.current = requestController;

        try {
            setError("");

            if (!forceRefresh) {
                setLoading(true);
            }

            const dataRoot = process.env.PUBLIC_URL || "";

            const response = await fetch(
                `${dataRoot}/fantasy_data/${userId}.json?t=${Date.now()}`,
                {
                    cache: "no-store",
                    signal: requestController.signal
                }
            );

            if (loggingOutRef.current) {
                return;
            }

            /*
                Un usuario nuevo puede no tener todavía un archivo
                de ligas. Se muestra el estado vacío.
            */
            if (response.status === 404) {
                setLeagues([]);
                return;
            }

            if (!response.ok) {
                throw new Error(
                    "No fue posible cargar las ligas. Código: " +
                        response.status
                );
            }

            const data = await response.json();

            if (loggingOutRef.current) {
                return;
            }

            setLeagues(getLeaguesFromData(data));
        } catch (requestError) {
            /*
                AbortError es esperado al cerrar sesión o actualizar
                nuevamente; no debe mostrar error al usuario.
            */
            if (
                requestError instanceof DOMException &&
                requestError.name === "AbortError"
            ) {
                return;
            }

            if (loggingOutRef.current) {
                return;
            }

            console.error(
                "No fue posible cargar las ligas Fantasy:",
                requestError
            );

            setError(
                "No fue posible cargar tus ligas. Intenta actualizar nuevamente."
            );
        } finally {
            if (
                leaguesRequestRef.current === requestController
            ) {
                leaguesRequestRef.current = null;
            }

            if (!loggingOutRef.current) {
                setLoading(false);
            }
        }
    },
    [router]
);


    useEffect(
        function () {
            loadLeagues();
        },
        [loadLeagues]
    );

    useEffect(function () {
        fantasyPushRegisterServiceWorker()
            .catch(function (error: unknown) {
                console.warn(
                    "No fue posible registrar el Service Worker:",
                    error
                );
            });
    }, []);


    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadLeagues(true);
        event.detail.complete();
    }

    async function joinLeague() {
    const normalizedLeagueId = leagueId.trim();

    const accessToken =
        Cookies.get("access_token") || "";

    const accessUsername =
        Cookies.get("access_username") || "";

    if (!normalizedLeagueId) {
        showToast(
            "Ingresa el identificador de la liga que deseas unirte.",
            "warning"
        );

        return;
    }

if (!accessToken || !accessUsername) {
    clearAuthentication();

    showToast(
        "Tu sesión expiró. Inicia sesión nuevamente.",
        "danger"
    );

    history.replace("/home");

    return;
}


    try {
        setJoiningLeague(true);

        /*
            Por ahora se crea el nombre del equipo usando el
            nombre público del usuario autenticado.

            Después puedes agregar un campo específico para que
            cada manager elija su nombre de equipo.
        */
        const teamName =
            getAccessUser().trim() || "Mi equipo";

        const response = await fetch(
            "https://fantasy.ftblscore.com/" +
                "create_league/control_newTeam.php",
            {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-Access-Token": accessToken,
                    "X-Access-Username": accessUsername
                },
                body: JSON.stringify({
                    leagueId: normalizedLeagueId,
                    teamName1: teamName
                })
            }
        );

        const rawResponse = await response.text();

        let result: {
            success?: boolean;
            message?: string;
        };

        try {
            result = JSON.parse(rawResponse);
        } catch {
            console.error(
                "control_newTeam.php no devolvió JSON:",
                rawResponse
            );

            throw new Error(
                "El servidor no devolvió una respuesta válida."
            );
        }

        if (!response.ok || !result.success) {
            throw new Error(
                result.message ||
                    "No fue posible unirse a esta liga."
            );
        }

        setLeagueId("");

        showToast(
            result.message ||
                "Te uniste correctamente a la liga.",
            "success"
        );

        await loadLeagues(true);
    } catch (requestError: unknown) {
        console.error(
            "No fue posible unirse a la liga:",
            requestError
        );

        showToast(
            requestError instanceof Error
                ? requestError.message
                : "No fue posible unirse a la liga.",
            "danger"
        );
    } finally {
        setJoiningLeague(false);
    }
}

async function copyMyUserId(): Promise<void> {
    const userId = getAccessToken();

    if (!userId) {
        showToast(
            "No fue posible obtener tu identificador. Inicia sesión nuevamente.",
            "danger"
        );

        return;
    }

    try {
        /*
            navigator.clipboard funciona en HTTPS y navegadores modernos.
        */
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(userId);
        } else {
            /*
                Compatibilidad con navegadores o WebViews antiguos.
            */
            const temporaryInput = document.createElement("textarea");

            temporaryInput.value = userId;
            temporaryInput.setAttribute("readonly", "");
            temporaryInput.style.position = "fixed";
            temporaryInput.style.opacity = "0";

            document.body.appendChild(temporaryInput);

            temporaryInput.select();

            const copied = document.execCommand("copy");

            document.body.removeChild(temporaryInput);

            if (!copied) {
                throw new Error("No se pudo copiar el identificador.");
            }
        }

        showToast(
            "Tu ID fue copiado. Compártelo con el comisionado.",
            "success"
        );
    } catch (copyError: unknown) {
        console.error(
            "No fue posible copiar el ID del usuario:",
            copyError
        );

        showToast(
            "No fue posible copiar tu ID automáticamente.",
            "danger"
        );
    }
}

function logOut(): void {
    if (loggingOutRef.current) {
        return;
    }

    loggingOutRef.current = true;
    setLoggingOut(true);

    /*
        Evita que una respuesta pendiente de fetch actualice
        Fantasy después de que el usuario cerró sesión.
    */
    leaguesRequestRef.current?.abort();
    leaguesRequestRef.current = null;

    setLeagues([]);
    setLeagueId("");
    setError("");

    /*
        Elimina cookies y notifica el cambio a Tabs.
    */
    clearAuthentication();

    /*
        history.replace cambia la ruta de forma directa.
        No deja Fantasy en el historial y evita que el usuario
        vuelva a esta pantalla mediante el botón atrás.
    */
    history.replace("/home");
}

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="fantasy-toolbar">
                    <IonTitle>Fantasy</IonTitle>

<IonButton
    slot="end"
    fill="clear"
    color="medium"
    aria-label="Copiar mi ID de usuario"
    title="Copiar mi ID"
    disabled={loggingOut}
    onClick={copyMyUserId}
>
    <IonIcon icon={copyOutline} />
</IonButton>

<IonButton
    slot="end"
    fill="clear"
    color="medium"
    aria-label="Cerrar sesión"
    disabled={joiningLeague || loggingOut}
    onClick={logOut}
>
    <IonIcon icon={logOutOutline} />
</IonButton>


                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="fantasy-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando tus ligas..."
                    />
                </IonRefresher>

                <section className="fantasy-hero">
                    <div className="fantasy-hero__icon">
                        <IonIcon icon={americanFootballOutline} />
                    </div>

                    <div>
                        <span>FANTASY MEXFBA</span>
                        <h1>Hola, {getAccessUser()}</h1>
                        <p>
                            Gestiona tus equipos, ligas y enfrentamientos.
                        </p>
                    </div>
                </section>

                <section className="fantasy-summary">
                    <div>
                        <IonIcon icon={trophyOutline} />
                        <span>LIGAS ACTIVAS</span>
                        <strong>{leagues.length}</strong>
                    </div>

                    <div>
                        <IonIcon icon={peopleOutline} />
                        <span>ESTADO</span>
                        <strong>Activo</strong>
                    </div>
                </section>

<section className="fantasy-user-id">
    <span className="fantasy-user-id__icon">
        <IonIcon icon={copyOutline} />
    </span>

    <div className="fantasy-user-id__content">
        <span>IDENTIFICADOR DE MANAGER</span>

        <strong>¿Te transferirán un equipo?</strong>

        <small>
            Copia tu ID y compártelo con el comisionado de la liga.
        </small>
    </div>

    <IonButton
        className="fantasy-user-id__button"
        fill="outline"
        size="small"
        disabled={loggingOut}
        onClick={copyMyUserId}
    >
        Copiar
    </IonButton>
</section>

<FantasyPushSettings
    credentials={{
        accessToken: getAccessToken(),
        accessUsername: getAccessUsername()
    }}
/>
                <section className="fantasy-section">
                    <div className="fantasy-section__heading">
                        <div>
                            <span>COMIENZA A JUGAR</span>
                            <h2>Tu próxima liga</h2>
                        </div>

                        <IonIcon icon={americanFootballOutline} />
                    </div>

                    <div className="fantasy-actions">
                        <button
                            className="fantasy-action fantasy-action--create"
                            type="button"
                            onClick={function () {
                                router.push(
                                    "/createLeague",
                                    "forward",
                                    "push"
                                );
                            }}
                        >
                            <span className="fantasy-action__icon">
                                <IonIcon icon={addOutline} />
                            </span>

                            <span>
                                <strong>Crear una liga</strong>
                                <small>
                                    Invita a tus amigos y define reglas.
                                </small>
                            </span>

                            <IonIcon
                                className="fantasy-action__arrow"
                                icon={arrowForwardOutline}
                            />
                        </button>

                        <div className="fantasy-join">
                            <div className="fantasy-join__heading">
                                <IonIcon icon={ticketOutline} />

                                <div>
                                    <strong>Unirse a una liga</strong>
                                    <span>
                                        Usa el identificador compartido por el
                                        comisionado.
                                    </span>
                                </div>
                            </div>

                            <IonItem
                                className="fantasy-join__input"
                                lines="none"
                            >
                                <IonLabel position="stacked">
                                    ID de liga
                                </IonLabel>

                                <IonInput
                                    value={leagueId}
                                    disabled={joiningLeague}
                                    placeholder="Ejemplo: LIGA-2026"
                                    onIonChange={function (event) {
                                        setLeagueId(
                                            event.detail.value || ""
                                        );
                                    }}
                                />
                            </IonItem>

                            <IonButton
                                className="fantasy-join__button"
                                expand="block"
                                fill="outline"
                                disabled={joiningLeague || loggingOut}
                                onClick={joinLeague}
                            >
                                {joiningLeague ? (
                                    <>
                                        <IonSpinner name="crescent" />
                                        Uniéndose...
                                    </>
                                ) : (
                                    <>
                                        Unirme a la liga
                                        <IonIcon
                                            slot="end"
                                            icon={arrowForwardOutline}
                                        />
                                    </>
                                )}
                            </IonButton>
                        </div>
                    </div>
                </section>

                <section className="fantasy-section fantasy-section--leagues">
                    <div className="fantasy-section__heading">
                        <div>
                            <span>MIS LIGAS</span>
                            <h2>Competencias activas</h2>
                        </div>

                        {!loading && !error && (
                            <IonBadge className="fantasy-count">
                                {leagues.length}
                            </IonBadge>
                        )}
                    </div>

                    {loading && (
                        <IonProgressBar
                            className="fantasy-progress"
                            type="indeterminate"
                        />
                    )}

                    {!loading && error && (
                        <div className="fantasy-empty">
                            <IonIcon icon={refreshOutline} />
                            <strong>No pudimos cargar tus ligas</strong>
                            <span>{error}</span>

                            <IonButton
                                fill="outline"
                                size="small"
                                onClick={function () {
                                    loadLeagues(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    )}

                    {!loading && !error && leagues.length === 0 && (
                        <div className="fantasy-empty">
                            <IonIcon icon={trophyOutline} />
                            <strong>Aún no tienes ligas</strong>
                            <span>
                                Crea una liga o únete con un identificador para
                                comenzar a jugar.
                            </span>
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        leagues.map(function (league) {
                            return (
                                <div
                                    className="fantasy-league"
                                    key={league.id}
                                >
                                    {ListContainer(
                                        league.id,
                                        "https://logowik.com/content/uploads/images/american-football546.logowik.com.webp",
                                        league.name
                                    )}
                                </div>
                            );
                        })}
                </section>
            </IonContent>
        </IonPage>
    );
};

export default Fantasy;
